import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { webhookService } from '../services/webhookService'

export const getWebhookSubscriptions = createAsyncThunk(
  'webhooks/getAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await webhookService.getSubscriptions()
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const createWebhookSubscription = createAsyncThunk(
  'webhooks/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await webhookService.createSubscription(data)
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const updateWebhookSubscription = createAsyncThunk(
  'webhooks/update',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await webhookService.updateSubscription(id, data)
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const deleteWebhookSubscription = createAsyncThunk(
  'webhooks/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await webhookService.deleteSubscription(id)
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const getWebhookDeliveries = createAsyncThunk(
  'webhooks/getDeliveries',
  async (subscriptionId, { rejectWithValue }) => {
    try {
      const response = await webhookService.getDeliveries(subscriptionId)
      return { subscriptionId, data: response }
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const testWebhookSubscription = createAsyncThunk(
  'webhooks/test',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await webhookService.testSubscription(id, body)
      return response
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

export const retryWebhookDelivery = createAsyncThunk(
  'webhooks/retry',
  async ({ deliveryId, subscriptionId }, { rejectWithValue }) => {
    try {
      const response = await webhookService.retryDelivery(deliveryId)
      return { subscriptionId, data: response }
    } catch (error) {
      return rejectWithValue(error?.response?.data)
    }
  }
)

const initialState = {
  subscriptions: [],
  deliveries: {},
  loading: false,
  deliveriesLoading: null,
  error: null,
  message: '',
}

const webhookSlice = createSlice({
  name: 'webhooks',
  initialState,
  reducers: {
    clearMessage: (state) => { state.message = '' },
    clearError: (state) => { state.error = null },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebhookSubscriptions.pending, (state) => { state.loading = true; state.error = null })
      .addCase(getWebhookSubscriptions.fulfilled, (state, action) => {
        state.loading = false
        const payload = action.payload
        state.subscriptions = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
      })
      .addCase(getWebhookSubscriptions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to load subscriptions'
      })

      .addCase(createWebhookSubscription.fulfilled, (state, action) => {
        state.message = action.payload?.message || 'Subscription created'
      })
      .addCase(createWebhookSubscription.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to create subscription'
      })

      .addCase(updateWebhookSubscription.fulfilled, (state, action) => {
        state.message = action.payload?.message || 'Subscription updated'
      })
      .addCase(updateWebhookSubscription.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update subscription'
      })

      .addCase(deleteWebhookSubscription.fulfilled, (state, action) => {
        state.message = action.payload?.message || 'Subscription deleted'
      })
      .addCase(deleteWebhookSubscription.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete subscription'
      })

      .addCase(getWebhookDeliveries.pending, (state, action) => {
        state.deliveriesLoading = action.meta.arg
      })
      .addCase(getWebhookDeliveries.fulfilled, (state, action) => {
        state.deliveriesLoading = null
        const { subscriptionId, data } = action.payload
        state.deliveries[subscriptionId] = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
      })
      .addCase(getWebhookDeliveries.rejected, (state) => {
        state.deliveriesLoading = null
      })

      .addCase(testWebhookSubscription.fulfilled, (state, action) => {
        state.message = action.payload?.message || 'Test event sent'
      })
      .addCase(testWebhookSubscription.rejected, (state, action) => {
        state.error = action.payload?.message || 'Test failed'
      })

      .addCase(retryWebhookDelivery.fulfilled, (state, action) => {
        state.message = action.payload?.data?.message || 'Retry queued'
      })
      .addCase(retryWebhookDelivery.rejected, (state, action) => {
        state.error = action.payload?.message || 'Retry failed'
      })
  },
})

export const { clearMessage, clearError } = webhookSlice.actions
export default webhookSlice.reducer
